import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { 
  Search, RefreshCw, Send, Calendar, ChevronRight, ChevronDown, ChevronUp,
  Phone, Mail, MessageSquare, User, Clock, X, Paperclip, 
  Bold, Italic, Underline, Link2, Smile, Plus, ArrowLeft, Archive, Trash2
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { formatEffortFromMinutes } from '../utils/effortFormatting';


// Utility function to convert 24-hour time to 12-hour format
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  
  try {
    // Handle both HH:MM and HH:MM:SS formats
    const [hours, minutes] = time24.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) return time24;
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (e) {
    return time24; // Return original if parsing fails
  }
};

const UnifiedInbox = () => {
  // State management
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Message expansion state for Gmail-style collapsing
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  
  // Day schedule state
  const [daySchedule, setDaySchedule] = useState([]);
  const [scheduleCollapsed, setScheduleCollapsed] = useState(true); // Default to collapsed for more inbox space
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Reply and compose
  const [replyContent, setReplyContent] = useState('');
  const [composeMode, setComposeMode] = useState(false);
  const [newMessage, setNewMessage] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    type: 'email',
    provider: 'gmail',
    attachments: []
  });
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [activeSearchField, setActiveSearchField] = useState(null); // 'to', 'cc', 'bcc'
  const [searchingContacts, setSearchingContacts] = useState(false);

  const messagesEndRef = useRef(null);
  const latestMessageRef = useRef(null);

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    let decoded = text;
    for (let i = 0; i < 3; i++) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = decoded;
      const newDecoded = textarea.value;
      if (newDecoded === decoded) break; // No more changes
      decoded = newDecoded;
    }
    return decoded;
  };

  // Helper function to convert URLs in text to clickable links
  const linkifyText = (text) => {
    if (!text) return text;
    
    // URL regex pattern - matches http://, https://, and www. URLs
    const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Create a regex with global flag to find all matches
    const regex = new RegExp(urlPattern);
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before the URL
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add the URL as a clickable link
      const url = match[0];
      const href = url.startsWith('www.') ? `https://${url}` : url;
      parts.push(
        <a
          key={match.index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
      );
      
      lastIndex = regex.lastIndex;
    }
    
    // Add remaining text after the last URL
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Helper function to strip HTML and clean email content
  const stripHtmlAndClean = (content) => {
    if (!content) return '';
    
    // Remove HTML tags
    const withoutHtml = content.replace(/<[^>]*>/g, '');
    
    // Decode HTML entities
    const decoded = decodeHtmlEntities(withoutHtml);
    
    // Clean up extra whitespace and normalize line breaks
    return decoded
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .trim();
  };

  // Helper function to detect and separate forwarded content
  const processEmailContent = (body) => {
    if (!body) return { cleanContent: 'This email has no content to display.', forwardedContent: '' };
    
    // 🎯 FIX: If body contains attachment metadata, extract actual email content
    if (body.includes('--- EMAIL ATTACHMENTS ---')) {
      // Split on the attachment separator and take the content before it
      const parts = body.split('--- EMAIL ATTACHMENTS ---');
      if (parts.length > 0) {
        const actualEmailContent = parts[0].trim();
        // If there's actual email content before the attachments, use it
        if (actualEmailContent && actualEmailContent.length > 20) {
          body = actualEmailContent;
        } else {
          // If no meaningful content before attachments, return user-friendly message
          return { 
            cleanContent: 'This email appears to contain only attachments. Please check the attachments section below for content.', 
            forwardedContent: '' 
          };
        }
      }
    }
    
    // Check if body is just whitespace/newlines
    if (!body.trim() || body.trim().length < 5) {
      return { 
        cleanContent: 'This email has no text content to display.', 
        forwardedContent: '' 
      };
    }
    
    // First, clean HTML content
    const cleanBody = stripHtmlAndClean(body);
    
    // Common forwarded email indicators
    const forwardedIndicators = [
      /^-+\s*Forwarded message\s*-+/im,
      /^From:\s*.+\nSent:\s*.+\nTo:\s*.+\nSubject:/im,
      /^On .+ wrote:/im,
      /^Begin forwarded message:/im,
      /^---------- Forwarded message ----------/im
    ];
    
    // Find where forwarded content starts
    let forwardStart = -1;
    for (const indicator of forwardedIndicators) {
      const match = cleanBody.match(indicator);
      if (match) {
        forwardStart = match.index;
        break;
      }
    }
    
    if (forwardStart === -1) {
      return { cleanContent: cleanBody, forwardedContent: '' };
    }
    
    return {
      cleanContent: cleanBody.substring(0, forwardStart).trim(),
      forwardedContent: cleanBody.substring(forwardStart).trim()
    };
  };

  // Helper function to get message preview (first line or snippet)
  const getMessagePreview = (message) => {
    const content = message.body || message.snippet || message.subject || 'No content';
    const { cleanContent } = processEmailContent(content);
    
    // Additional HTML stripping in case processEmailContent didn't catch everything
    const fullyClean = cleanContent.replace(/<[^>]*>/g, '').trim();
    
    // Decode HTML entities multiple times (like &#39; to ', &amp; to &, etc.)
    let decoded = fullyClean;
    for (let i = 0; i < 3; i++) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = decoded;
      const newDecoded = textarea.value;
      if (newDecoded === decoded) break; // No more changes
      decoded = newDecoded;
    }
    
    // Get first meaningful line (skip empty lines)
    const lines = decoded.split('\n').filter(line => line.trim());
    const firstLine = lines[0] || decoded.substring(0, 100);
    
    // Truncate if too long
    return firstLine.length > 80 ? firstLine.substring(0, 80) + '...' : firstLine;
  };

  // Toggle message expansion
  const toggleMessageExpansion = (messageId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Handle attachment click (download or preview)
  const handleAttachmentClick = async (attachment, message) => {
    try {
      if (attachment.download_url) {
        // For larger attachments with download URLs
        const response = await api.get(attachment.download_url, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` },
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.filename || 'attachment';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else if (attachment.content) {
        // For inline attachments or small downloaded attachments
        try {
          const binaryString = atob(attachment.content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          const blob = new Blob([bytes], { type: attachment.mime_type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = attachment.filename || 'attachment';
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error processing attachment content:', error);
          alert('Unable to process attachment content');
        }
      } else {
        alert('Attachment content not available');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Auto-scroll to bottom of conversation when messages load (like messaging apps)
  useEffect(() => {
    if (conversationMessages.length > 0) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        // Find the scrollable messages container
        const messagesContainer = document.querySelector('.overflow-y-auto');
        
        if (messagesContainer) {
          // Scroll to bottom using scrollTop
          messagesContainer.scrollTo({
            top: messagesContainer.scrollHeight,
            behavior: 'smooth'
          });
        }
        
        // Backup method using messagesEndRef
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          });
        }
      });
    }
  }, [conversationMessages]);

  useEffect(() => {
    fetchConversations();
    fetchDaySchedule();
  }, [filterProvider, filterType, selectedDate, showArchived]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await api.get(`/inbox/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { 
          limit: 50,
          include_archived: showArchived
        }
      });
      
      if (response.data.success) {
        const conversationList = response.data.conversations || [];
        
        // Use the previews already provided by the backend (no need to fetch thread messages)
        const conversationsWithPreviews = conversationList.map(conversation => ({
          ...conversation,
          // Use backend-provided preview (subject for emails, message content for SMS)
          last_message_preview: conversation.snippet || conversation.last_message_preview || 'No preview available',
          last_message_direction: 'inbound' // Default direction for preview display
        }));
        
        setConversations(conversationsWithPreviews);
      }
      
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDaySchedule = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      const response = await api.get(`/schedule/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          date: dateStr,
          view: 'day'
        }
      });
      
      if (response.data.tasks) {
        // Filter tasks for selected date
        const dayTasks = response.data.tasks.filter(task => {
          if (!task.due_date) return false;
          const taskDate = new Date(task.due_date).toDateString();
          return taskDate === selectedDate.toDateString();
        });
        
        setDaySchedule(dayTasks);
      }
      
    } catch (error) {
      console.error('Failed to fetch day schedule:', error);
    }
  };

  const selectConversation = async (conversation) => {
    // Clear previous conversation state immediately to prevent display issues
    setConversationMessages([]);
    setExpandedMessages(new Set());
    
    setSelectedConversation(conversation);
    setLoadingMessages(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await api.get(`/inbox/thread/${conversation.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('🔍 Conversation Data:', {
        threadId: conversation.id,
        contactName: conversation.contact_name,
        messageCount: response.data.messages?.length || 0,
        messages: response.data.messages?.map(m => ({
          id: m.id,
          subject: m.subject,
          body: m.body?.substring(0, 100) + '...',
          from: m.from_address,
          to: m.to_address,
          direction: m.direction
        }))
      });
      
      if (response.data.success) {
        const messages = response.data.messages || [];
        setConversationMessages(messages);
        
        // Automatically mark all unread messages as read when opening conversation
        if (conversation.unread_count > 0) {
          const unreadMessageIds = messages
            .filter(msg => !msg.is_read)
            .map(msg => msg.id);
          
          // Mark as read in background (optimistic update)
          if (unreadMessageIds.length > 0) {
            // Update local state immediately
            setConversations(prev => prev.map(conv => 
              conv.id === conversation.id 
                ? { ...conv, unread_count: 0, is_read: true }
                : conv
            ));
            
            // Update backend in background
            Promise.all(
              unreadMessageIds.map(msgId =>
                api.put(`/inbox/messages/${msgId}/read?read=true`, {}, {
                  headers: { 'Authorization': `Bearer ${token}` }
                }).catch(err => console.error(`Failed to mark message ${msgId} as read:`, err))
              )
            );
          }
        }
        
        // Smart expansion logic: expand only the most recent message
        const newExpandedMessages = new Set();
        if (messages.length > 0) {
          // Expand the most recent message (last in chronological order)
          const latestMessage = messages[messages.length - 1];
          newExpandedMessages.add(latestMessage.id);
        }
        setExpandedMessages(newExpandedMessages);
        
        // Instantly scroll to bottom when opening a new conversation
        // Use multiple approaches with better timing
        const scrollToBottom = () => {
          // Find the messages container (should be the scrollable parent)
          const messagesContainer = document.querySelector('.overflow-y-auto');
          
          if (messagesContainer) {
            // Method 1: Set scrollTop to maximum
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
          
          // Method 2: Use messagesEndRef as backup
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ 
              behavior: 'auto',
              block: 'end',
              inline: 'nearest'
            });
          }
        };
        
        // Try immediately
        setTimeout(scrollToBottom, 50);
        // Try again after a longer delay to ensure DOM is ready
        setTimeout(scrollToBottom, 200);
        // Final attempt with longer delay
        setTimeout(scrollToBottom, 500);
      }
      
      await markConversationAsRead(conversation);
      
    } catch (error) {
      console.error('Failed to load conversation messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const markConversationAsRead = async (conversation) => {
    try {
      const token = localStorage.getItem('access_token');
      
      await api.post(`/inbox/mark-thread-read/${conversation.id}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversation.id 
            ? { ...conv, unread_count: 0 }
            : conv
        )
      );
      
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleArchiveConversation = async (conversationId, archive = true) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Optimistically remove from UI immediately for instant feedback
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // If this was the selected conversation, deselect it
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }
      
      // Archive in background
      const response = await api.get(`/inbox/thread/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.messages) {
        // Archive all messages in the thread
        const archivePromises = response.data.messages.map(msg =>
          api.put(`/inbox/messages/${msg.id}/archive?archive=${archive}`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(err => {
            console.error(`Failed to archive message ${msg.id}:`, err);
            return null; // Continue with other messages even if one fails
          })
        );
        
        await Promise.all(archivePromises);
      }
      
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      // On error, refresh to restore correct state
      await fetchConversations();
    }
  };

  const handleArchiveMessage = async (messageId, archive = true) => {
    try {
      const token = localStorage.getItem('access_token');
      
      await api.put(`/inbox/messages/${messageId}/archive?archive=${archive}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Refresh conversations to reflect the change
      await fetchConversations();
      
      // If we archived the selected conversation, deselect it
      if (archive && selectedConversation) {
        // Get all message IDs from the current conversation
        const conversationMessageIds = conversationMessages.map(m => m.id);
        if (conversationMessageIds.includes(messageId)) {
          setSelectedConversation(null);
        }
      }
      
    } catch (error) {
      console.error('Failed to archive message:', error);
      alert(`Failed to ${archive ? 'archive' : 'unarchive'} message`);
    }
  };

  const handleMarkAsRead = async (messageId, read = true) => {
    try {
      const token = localStorage.getItem('access_token');
      
      await api.put(`/inbox/messages/${messageId}/read?read=${read}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update the message in conversationMessages
      setConversationMessages(prev =>
        prev.map(msg =>
          msg.id === messageId ? { ...msg, is_read: read } : msg
        )
      );
      
      // Refresh conversations to update unread counts
      await fetchConversations();
      
    } catch (error) {
      console.error('Failed to update read status:', error);
      alert(`Failed to mark message as ${read ? 'read' : 'unread'}`);
    }
  };


  const sendReply = async () => {
    if (!selectedConversation || !replyContent.trim()) return;

    try {
      const token = localStorage.getItem('access_token');
      
      const replyData = {
        provider: selectedConversation.provider,
        to: selectedConversation.contact_phone || selectedConversation.contact_email,
        body: replyContent
      };

      const response = await api.post(`/inbox/send`, replyData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setReplyContent('');
        // Refresh conversation to show sent message
        await selectConversation(selectedConversation);
        await fetchConversations();
      } else {
        // Handle API errors (integration not connected, etc.)
        const error = response.data.result?.error || 'Failed to send reply';
        console.error('Reply send failed:', error);
        
        if (response.data.result?.requires_integration) {
          // Show integration setup guidance in UI instead of alert
          setReplyContent(`❌ ${error}\n\nPlease set up the integration in Settings → Personal Integrations.`);
        } else {
          setReplyContent(`❌ Error: ${error}`);
        }
      }
      
    } catch (error) {
      console.error('Failed to send reply:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send reply';
      setReplyContent(`❌ Error sending reply: ${errorMessage}`);
    }
  };

  // Contact search functionality (reusable for To, CC, BCC)
  const searchContacts = async (query, field = 'to') => {
    console.log(`🔍 searchContacts called with query: "${query}", field: "${field}"`);
    
    if (!query || query.length < 2) {
      console.log('Query too short, clearing suggestions');
      setContactSuggestions([]);
      setShowContactSuggestions(false);
      setActiveSearchField(null);
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('No auth token found');
      setContactSuggestions([{
        id: 'no_auth',
        name: 'Not Authenticated',
        primary_contact: 'Please login to search contacts',
        display_name: 'Please login to search contacts'
      }]);
      setShowContactSuggestions(true);
      return;
    }

    console.log('Starting contact search...');
    setSearchingContacts(true);
    setActiveSearchField(field);
    
    try {
      const searchUrl = `/contacts/search?query=${encodeURIComponent(query)}&limit=15`;
      console.log(`Search URL: ${searchUrl}`);
      
      const response = await api.get(searchUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log(`API Response status: ${response.status}`);
      console.log('API Response data:', response.data);
      
      const contacts = response.data.contacts || [];
      console.log(`Found ${contacts.length} contacts`);
      
      if (contacts.length === 0) {
        // Show a "no results" message
        setContactSuggestions([{
          id: 'no_results',
          name: 'No Results',
          primary_contact: `No contacts found for "${query}"`,
          display_name: `No contacts found for "${query}"`
        }]);
      } else {
        setContactSuggestions(contacts);
      }
      
      setShowContactSuggestions(true);
      console.log(`Set showContactSuggestions to true, activeSearchField to: ${field}`);
      
    } catch (error) {
      console.error('Contact search failed:', error);
      console.error('Error details:', error.response?.data);
      
      if (error.response?.status === 401) {
        console.log('Authentication error - token may be expired');
        // Show a helpful message to the user
        setContactSuggestions([{
          id: 'auth_error',
          name: 'Authentication Error',
          primary_contact: 'Please refresh the page and login again',
          display_name: 'Authentication Error - Please refresh and login again'
        }]);
        setShowContactSuggestions(true);
      } else {
        setContactSuggestions([{
          id: 'error',
          name: 'Search Error',
          primary_contact: 'Failed to search contacts',
          display_name: 'Failed to search contacts'
        }]);
        setShowContactSuggestions(true);
      }
    } finally {
      setSearchingContacts(false);
      console.log('Search completed, searchingContacts set to false');
    }
  };

  const selectContact = (contact, field = 'to') => {
    // Don't select error messages or special contacts
    if (contact.id && (contact.id.includes('error') || contact.id.includes('no_') || contact.id.includes('auth'))) {
      return;
    }
    
    const contactValue = contact.primary_contact;
    setNewMessage(prev => ({ ...prev, [field]: contactValue }));
    setShowContactSuggestions(false);
    setContactSuggestions([]);
    setActiveSearchField(null);
  };

  const handleContactFieldBlur = () => {
    // Delay hiding suggestions to allow clicks
    setTimeout(() => {
      setShowContactSuggestions(false);
      setActiveSearchField(null);
    }, 200);
  };

  // File upload handling
  const onDrop = useCallback((acceptedFiles) => {
    const newAttachments = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setNewMessage(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments]
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: 25 * 1024 * 1024, // 25MB
  });

  const removeAttachment = (attachmentId) => {
    setNewMessage(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== attachmentId)
    }));
  };

  // Rich text editor functionality
  const editorRef = useRef(null);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      // Update state with current content
      setTimeout(() => {
        setNewMessage(prev => ({ 
          ...prev, 
          body: editorRef.current?.innerHTML || '' 
        }));
      }, 10);
    }
  }, []);

  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  const handleEditorInput = useCallback(() => {
    if (editorRef.current) {
      setNewMessage(prev => ({ 
        ...prev, 
        body: editorRef.current?.innerHTML || '' 
      }));
    }
  }, []);

  const handleEditorPaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleEditorInput();
  }, [handleEditorInput]);

  // Initialize editor content when compose mode opens
  useEffect(() => {
    if (composeMode && editorRef.current && newMessage.type === 'email') {
      editorRef.current.innerHTML = newMessage.body || '';
    }
  }, [composeMode, newMessage.type]);

  const sendMessage = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const messageData = {
        provider: newMessage.provider,
        to: newMessage.to,
        cc: newMessage.cc || undefined,
        bcc: newMessage.bcc || undefined,
        subject: newMessage.type === 'email' ? newMessage.subject : undefined,
        body: newMessage.body
      };

      const response = await api.post(`/inbox/send`, messageData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setComposeMode(false);
        setShowCcBcc(false);
        setNewMessage({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          body: '',
          type: 'email',
          provider: 'gmail',
          attachments: []
        });
        
        // Refresh conversations to show new sent message
        fetchConversations();
      } else {
        // Handle API errors - show in the compose body field
        const error = response.data.result?.error || 'Failed to send message';
        console.error('Message send failed:', error);
        
        if (response.data.result?.requires_integration) {
          setNewMessage(prev => ({
            ...prev,
            body: `❌ ${error}\n\nPlease set up the integration in Settings → Personal Integrations.\n\n${prev.body}`
          }));
        } else {
          setNewMessage(prev => ({
            ...prev,
            body: `❌ Error: ${error}\n\n${prev.body}`
          }));
        }
      }
      
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send message';
      setNewMessage(prev => ({
        ...prev,
        body: `❌ Error sending message: ${errorMessage}\n\n${prev.body}`
      }));
    }
  };

  // Format timestamp for Gmail-style display
  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      // Check if message is from today - show time only
      if (messageDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
      }
      
      // Check if message is from yesterday
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (messageDate.getTime() === yesterday.getTime()) {
        return 'Yesterday';
      }
      
      // Check if message is from this year - show month and day
      if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric'
        });
      }
      
      // Messages from previous years - show month, day, year
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return '';
    }
  };

  const formatTaskTime = (task) => {
    if (task.due_time) {
      return formatTimeTo12Hour(task.due_time);
    } else if (task.scheduled_time) {
      return new Date(task.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return 'All day';
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'gohighlevel':
        return <MessageSquare className="w-4 h-4 text-green-600" />;
      case 'gmail':
        return <Mail className="w-4 h-4 text-red-600" />;
      case 'outlook':
        return <Mail className="w-4 h-4 text-blue-600" />;
      default:
        return <Mail className="w-4 h-4 text-gray-600" />;
    }
  };

  const getMessageTypeIcon = (type) => {
    return type === 'sms' 
      ? <MessageSquare className="w-4 h-4" />
      : <Mail className="w-4 h-4" />;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'text-red-600 bg-red-100',
      'High': 'text-orange-600 bg-orange-100', 
      'Normal': 'text-blue-600 bg-blue-100',
      'Low': 'text-green-600 bg-green-100'
    };
    return colors[priority] || colors['Normal'];
  };

  const filteredConversations = conversations.filter(conv => {
    // Filter by search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = (
        conv.contact_name.toLowerCase().includes(searchLower) ||
        (conv.contact_email && conv.contact_email.toLowerCase().includes(searchLower)) ||
        (conv.contact_phone && conv.contact_phone.includes(searchQuery)) ||
        (conv.subject && conv.subject.toLowerCase().includes(searchLower))
      );
      if (!matchesSearch) return false;
    }
    
    // Filter by provider
    if (filterProvider !== 'all' && conv.provider !== filterProvider) {
      return false;
    }
    
    // Filter by type
    if (filterType !== 'all' && conv.type !== filterType) {
      return false;
    }
    
    return true;
  });

  return (
    <div className="h-full w-full flex bg-gray-50">
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] {
          outline: none;
        }
        [contenteditable]:focus {
          outline: none;
        }
        [contenteditable] p {
          margin: 0.5em 0;
        }
        [contenteditable] ul, [contenteditable] ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
      `}</style>
      {/* Main Content Area - Dynamic width based on schedule visibility */}
      <div className={`flex-1 flex ${scheduleCollapsed ? 'w-full' : ''} transition-all duration-300`}>
        {!selectedConversation ? (
          /* Gmail-Style List View */
          <div className="w-full bg-white border border-gray-200 rounded-lg flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-semibold text-gray-900">Inbox</h1>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={fetchConversations}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setComposeMode(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Compose</span>
                  </button>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="flex flex-col space-y-3">
                <div className="flex space-x-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <select
                    value={filterProvider}
                    onChange={(e) => setFilterProvider(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Providers</option>
                    <option value="gohighlevel">GoHighLevel</option>
                    <option value="gmail">Gmail</option>
                    <option value="outlook">Outlook</option>
                  </select>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="sms">SMS</option>
                    <option value="email">Email</option>
                  </select>
                </div>
                
                {/* Show Archived Toggle */}
                <div className="flex items-center space-x-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Show Archived</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    ({filteredConversations.length} conversations)
                  </span>
                </div>
              </div>
            </div>

            {/* Gmail-Style Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading conversations...</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>No conversations found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`px-6 py-4 hover:bg-gray-50 transition-colors group ${
                        selectedConversation?.id === conversation.id
                          ? 'bg-blue-50'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div 
                          className="flex items-center space-x-4 flex-1 min-w-0 cursor-pointer"
                          onClick={() => selectConversation(conversation)}
                        >
                          <div className="flex-shrink-0">
                            {getProviderIcon(conversation.provider)}
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Gmail-style layout: Name, Subject, Time */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <h3 className={`text-base ${
                                  conversation.unread_count > 0 
                                    ? 'font-bold text-gray-900' 
                                    : 'font-normal text-gray-700'
                                }`}>
                                  {conversation.contact_name || conversation.from_address || 'Unknown'}
                                </h3>
                                {conversation.message_count > 1 && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                    {conversation.message_count}
                                  </span>
                                )}
                              </div>
                              
                              {/* Time display */}
                              <div className={`text-sm flex-shrink-0 ml-4 ${
                                conversation.unread_count > 0 ? 'text-gray-900 font-semibold' : 'text-gray-500'
                              }`}>
                                {formatMessageDate(conversation.last_message_at)}
                              </div>
                            </div>
                            
                            {/* Subject and preview line */}
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm truncate ${
                                  conversation.unread_count > 0 
                                    ? 'text-gray-900 font-semibold' 
                                    : 'text-gray-700 font-normal'
                                }`}>
                                  {conversation.type === 'email' ? (
                                    conversation.subject || 'No Subject'
                                  ) : (
                                    'SMS Conversation'
                                  )}
                                </p>
                                {conversation.type === 'email' && conversation.last_message_preview && (
                                  <p className={`text-sm truncate mt-1 ${
                                    conversation.unread_count > 0 ? 'text-gray-700' : 'text-gray-500'
                                  }`}>
                                    {decodeHtmlEntities(conversation.last_message_preview)}
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                                {conversation.attachment_count > 0 && (
                                  <div className="flex items-center text-gray-400">
                                    <Paperclip className="w-4 h-4" />
                                  </div>
                                )}
                                {conversation.unread_count > 0 && (
                                  <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-1 font-semibold">
                                    {conversation.unread_count}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Action buttons - visible on hover */}
                        <div className="flex items-center space-x-1 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Toggle read/unread status
                              const newReadState = conversation.unread_count > 0; // If has unread, mark as read
                              
                              // Optimistically update UI
                              setConversations(prev => prev.map(conv => 
                                conv.id === conversation.id 
                                  ? { ...conv, unread_count: newReadState ? 0 : 1, is_read: newReadState }
                                  : conv
                              ));
                              
                              // Update backend - get thread messages first
                              api.get(`/inbox/thread/${conversation.id}`, {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                              }).then(response => {
                                if (response.data.success && response.data.messages) {
                                  const messageIds = response.data.messages.map(m => m.id);
                                  Promise.all(
                                    messageIds.map(msgId =>
                                      api.put(`/inbox/messages/${msgId}/read?read=${newReadState}`, {}, {
                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                                      }).catch(err => console.error(`Failed to update read status:`, err))
                                    )
                                  );
                                }
                              }).catch(err => {
                                console.error('Failed to get thread messages:', err);
                                // Revert on error
                                fetchConversations();
                              });
                            }}
                            className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                            title={conversation.unread_count > 0 ? "Mark as read" : "Mark as unread"}
                          >
                            <Mail className="w-4 h-4 text-gray-600 hover:text-gray-900" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveConversation(conversation.id, true);
                            }}
                            className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                            title="Archive conversation"
                          >
                            <Archive className="w-4 h-4 text-gray-600 hover:text-gray-900" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Split View - Conversation + Schedule */
          <>
            {/* Left Panel - Conversation List (Fixed width) */}
            <div className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
              {/* Compact Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h1 className="text-lg font-semibold text-gray-900">Inbox</h1>
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="p-2 text-blue-600 hover:text-blue-700 rounded-lg hover:bg-blue-50"
                    title="Back to full view"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Compact List */}
              <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-gray-100">
                  {filteredConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => selectConversation(conversation)}
                      className={`p-3 cursor-pointer hover:bg-gray-50 ${
                        selectedConversation?.id === conversation.id
                          ? 'bg-blue-50 border-r-2 border-blue-500'
                          : ''
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {getProviderIcon(conversation.provider)}
                        <div className="flex-1 min-w-0">
                          {/* Gmail-style display: Name • Subject • Time */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <h3 className={`text-sm truncate ${
                                  conversation.unread_count > 0 
                                    ? 'font-bold text-gray-900' 
                                    : 'font-normal text-gray-700'
                                }`}>
                                  {conversation.contact_name || conversation.from_address || 'Unknown'}
                                </h3>
                                {conversation.message_count > 1 && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                    {conversation.message_count}
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex items-center space-x-1 mt-1">
                                <p className={`text-xs truncate flex-1 ${
                                  conversation.unread_count > 0 
                                    ? 'text-gray-900 font-semibold' 
                                    : 'text-gray-600 font-normal'
                                }`}>
                                  {conversation.type === 'email' ? (
                                    conversation.subject || 'No Subject'
                                  ) : (
                                    decodeHtmlEntities(conversation.last_message_preview || conversation.snippet || 'No message preview')
                                  )}
                                </p>
                                
                                {conversation.attachment_count > 0 && (
                                  <div className="flex items-center space-x-1 text-gray-400">
                                    <Paperclip className="w-3 h-3" />
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Time display on the right */}
                            <div className={`text-xs ml-3 flex-shrink-0 ${
                              conversation.unread_count > 0 ? 'text-gray-900 font-semibold' : 'text-gray-500'
                            }`}>
                              {formatMessageDate(conversation.last_message_at)}
                            </div>
                          </div>
                        </div>
                        {conversation.unread_count > 0 && (
                          <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5 font-semibold">
                            {conversation.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Conversation Detail Panel - Expands to fill remaining space */}
            <div className="flex-1 flex flex-col bg-white">
              {/* Conversation Header */}
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    {/* Display conversation title differently for emails vs SMS */}
                    {selectedConversation.type === 'email' ? (
                      // GMAIL-STYLE HEADER: Subject as main title
                      <>
                        <h2 className="text-lg font-normal text-gray-900 mb-1">
                          {selectedConversation.subject || 'No Subject'}
                        </h2>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <span>
                            {selectedConversation.message_count} message{selectedConversation.message_count > 1 ? 's' : ''}
                          </span>
                          <span>•</span>
                          <span>{selectedConversation.contact_name || selectedConversation.from_address}</span>
                        </div>
                      </>
                    ) : (
                      // SMS HEADER: Show contact name as main title
                      <>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {selectedConversation.contact_name || selectedConversation.from_address || 'Unknown Contact'}
                        </h2>
                        <p className="text-sm text-gray-600">
                          Text Message Conversation
                        </p>
                      </>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                      <div className="flex items-center space-x-1">
                        {getProviderIcon(selectedConversation.provider)}
                        <span className="capitalize">{selectedConversation.provider}</span>
                      </div>
                      <span>{selectedConversation.message_count} messages</span>
                    </div>
                  </div>
                  
                  {/* Gmail-style action buttons */}
                  <div className="flex items-center space-x-2">
                    {selectedConversation.type === 'email' && (
                      <>
                        <button 
                          className="p-2 hover:bg-gray-100 rounded-md transition-colors" 
                          title="Archive"
                          onClick={() => {
                            handleArchiveConversation(selectedConversation.id, true);
                          }}
                        >
                          <Archive className="w-5 h-5 text-gray-500" />
                        </button>
                        <button 
                          className="p-2 hover:bg-gray-100 rounded-md transition-colors" 
                          title="Mark as unread"
                          onClick={() => {
                            // Mark the most recent message as unread
                            const latestMessage = conversationMessages[conversationMessages.length - 1];
                            if (latestMessage) {
                              handleMarkAsRead(latestMessage.id, false);
                            }
                          }}
                        >
                          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button 
                          className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                          onClick={() => setShowComposeModal(true)}
                        >
                          Reply
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => setSelectedConversation(null)}
                      className="p-2 hover:bg-gray-100 rounded-md transition-colors ml-2"
                      title="Close conversation"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages - Uses full available width */}
              <div className={`flex-1 overflow-y-auto px-6 py-4 ${
                selectedConversation?.type === 'sms' 
                  ? 'bg-gray-100' // SMS gets a gray background like messaging apps
                  : 'bg-gray-50'  // Emails keep the original background
              }`}>
                {loadingMessages ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading messages...</p>
                  </div>
                ) : (
                  <div className="space-y-2 w-full">
                    {/* Thread Subject Header - shown once at top */}
                    {conversationMessages.length > 0 && (
                      <div className="mb-6 pb-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">
                          {conversationMessages.find(m => m.subject)?.subject || selectedConversation?.contact_name || 'Conversation'}
                        </h2>
                        <div className="flex items-center text-sm text-gray-600 space-x-4">
                          <span>{conversationMessages.length} messages</span>
                          <span>•</span>
                          <span>{selectedConversation?.contact_name || selectedConversation?.contact_email}</span>
                          {selectedConversation?.contact_phone && (
                            <>
                              <span>•</span>
                              <span>{selectedConversation.contact_phone}</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {conversationMessages.map((message, index) => {
                      const isLatestMessage = index === conversationMessages.length - 1;
                      const isExpanded = expandedMessages.has(message.id);
                      const messageContent = message.body || message.snippet || message.subject || 'No content';
                      const { cleanContent, forwardedContent } = processEmailContent(messageContent);
                      const isUserMessage = message.direction === 'outbound';
                      const isSmsConversation = selectedConversation?.type === 'sms';
                      
                      // Debug email content issues
                      if (!isSmsConversation && isLatestMessage) {
                        console.log('🔍 Email Content Debug:', {
                          messageId: message.id,
                          subject: message.subject,
                          bodyLength: message.body?.length || 0,
                          bodyPreview: message.body?.substring(0, 200) + '...',
                          snippetLength: message.snippet?.length || 0,
                          snippetPreview: message.snippet?.substring(0, 100) + '...',
                          messageContentLength: messageContent?.length || 0,
                          cleanContentLength: cleanContent?.length || 0,
                          cleanContentPreview: cleanContent?.substring(0, 200) + '...',
                          isExpanded,
                          isLatestMessage,
                          from: message.from_address,
                          to: message.to_address,
                          containsAttachmentData: message.body?.includes('EMAIL ATTACHMENTS') || false,
                          containsAIAnalysis: message.body?.includes('AI Analysis') || false
                        });
                      }
                      
                      return (
                        <div
                          key={message.id}
                          ref={isLatestMessage ? latestMessageRef : null}
                          className={`w-full ${isLatestMessage ? 'mb-4' : 'mb-3'}`}
                        >
                          {isSmsConversation ? (
                            // SMS/Text Message Style - Clean and Simple
                            <div className="mb-4">
                              {/* Show date header only if different from previous message */}
                              {(() => {
                                const currentDate = new Date(message.timestamp).toDateString();
                                const prevMessage = conversationMessages[index - 1];
                                const prevDate = prevMessage ? new Date(prevMessage.timestamp).toDateString() : null;
                                const showDateHeader = !prevMessage || currentDate !== prevDate;
                                
                                return showDateHeader ? (
                                  <div className="text-center mb-3">
                                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
                                      {formatMessageDate(message.timestamp)}
                                    </span>
                                  </div>
                                ) : null;
                              })()}
                              
                              {/* Message bubble */}
                              <div className={`flex ${isUserMessage ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[75%] ${
                                  isUserMessage
                                    ? 'bg-blue-500 text-white rounded-2xl rounded-br-md'
                                    : 'bg-gray-200 text-gray-900 rounded-2xl rounded-bl-md'
                                } px-4 py-3 shadow-sm`}>
                                  {/* Message Content - Clean text only */}
                                  <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                    {linkifyText(cleanContent) || 'SMS Message'}
                                  </div>
                                  
                                  {/* SMS Attachments - Simplified */}
                                  {message.attachments && message.attachments.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-300 border-opacity-30">
                                      {message.attachments.map((attachment, attIndex) => (
                                        <div
                                          key={attIndex}
                                          className={`flex items-center space-x-2 p-2 rounded-lg cursor-pointer ${
                                            isUserMessage ? 'bg-blue-400 hover:bg-blue-300' : 'bg-gray-300 hover:bg-gray-400'
                                          } transition-colors`}
                                          onClick={() => handleAttachmentClick(attachment, message)}
                                        >
                                          <Paperclip className={`w-3 h-3 ${
                                            isUserMessage ? 'text-blue-100' : 'text-gray-600'
                                          }`} />
                                          <span className={`text-xs font-medium truncate ${
                                            isUserMessage ? 'text-blue-100' : 'text-gray-700'
                                          }`}>
                                            {attachment.filename || 'Attachment'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Email Style - More Detailed View
                            <>
                              {!isExpanded && !isLatestMessage ? (
                                // Gmail-style collapsed view for older emails
                                <div
                                  onClick={() => toggleMessageExpansion(message.id)}
                                  className="group border-l-4 border-gray-200 pl-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors mb-2"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      {/* Gmail-style collapsed header */}
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center space-x-3">
                                          {/* Small avatar for collapsed view */}
                                          <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                            <span className="text-xs font-medium text-gray-600">
                                              {isUserMessage ? 'Y' : (message.contact_name || message.from_address || 'C')[0].toUpperCase()}
                                            </span>
                                          </div>
                                          <span className="font-medium text-sm text-gray-800">
                                            {isUserMessage ? 'You' : (message.contact_name || message.from_address?.split('@')[0] || 'Contact')}
                                          </span>
                                        </div>
                                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                          {formatMessageDate(message.timestamp)}
                                        </span>
                                      </div>
                                      
                                      {/* Content preview - first line only */}
                                      <div className="text-sm text-gray-600 line-clamp-1">
                                        {getMessagePreview(message)}
                                      </div>
                                      
                                      {/* Attachment indicator */}
                                      {message.attachments && message.attachments.length > 0 && (
                                        <div className="flex items-center space-x-1 mt-1">
                                          <Paperclip className="w-3 h-3 text-gray-400" />
                                          <span className="text-xs text-gray-500">
                                            {message.attachments.length} attachment{message.attachments.length > 1 ? 's' : ''}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Expand chevron */}
                                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0 ml-2 transition-colors" />
                                  </div>
                                </div>
                              ) : (
                                // Gmail-style expanded email view
                                <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-4 overflow-hidden">
                                  {/* Email Header */}
                                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        {/* Gmail-style sender info */}
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center space-x-2">
                                            <div className="flex items-center space-x-2">
                                              {/* Sender avatar placeholder */}
                                              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-medium text-gray-600">
                                                  {isUserMessage ? 'Y' : (message.contact_name || message.from_address || 'C')[0].toUpperCase()}
                                                </span>
                                              </div>
                                              <div>
                                                <div className="font-medium text-gray-900">
                                                  {isUserMessage ? 'You' : (message.contact_name || message.from_address?.split('@')[0] || 'Contact')}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                  to {isUserMessage ? (message.to_address?.split('@')[0] || 'me') : 'me'}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Timestamp on the right */}
                                          <div className="text-xs text-gray-500">
                                            {formatMessageDate(message.timestamp)}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Actions and collapse button */}
                                      <div className="flex items-center space-x-2">
                                        {!isLatestMessage && (
                                          <button
                                            onClick={() => toggleMessageExpansion(message.id)}
                                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                                            title="Collapse message"
                                          >
                                            <ChevronUp className="w-4 h-4 text-gray-500" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Email Body */}
                                  <div className="px-4 py-4">
                                    {/* Display the actual email content */}
                                    {cleanContent && (
                                      <div className="text-sm leading-relaxed whitespace-pre-wrap break-words prose prose-sm max-w-none">
                                        {linkifyText(cleanContent)}
                                      </div>
                                    )}
                                    
                                    {/* Email Message Attachments */}
                                    {message.attachments && message.attachments.length > 0 && (
                                      <div className="mt-4 pt-4 border-t border-gray-200">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <Paperclip className="w-4 h-4 text-gray-500" />
                                          <span className="text-sm font-medium text-gray-700">
                                            {message.attachments.length} attachment{message.attachments.length > 1 ? 's' : ''}
                                          </span>
                                        </div>
                                        <div className="space-y-2">
                                          {message.attachments.map((attachment, attIndex) => (
                                            <div
                                              key={attIndex}
                                              className="p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                              <div 
                                                className="flex items-center justify-between cursor-pointer"
                                                onClick={() => handleAttachmentClick(attachment, message)}
                                              >
                                                <div className="flex items-center space-x-3 min-w-0 flex-1">
                                                  <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                                  <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium text-gray-900 truncate">
                                                      {attachment.filename || 'Unknown file'}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                      {attachment.content_type || 'unknown type'}
                                                      {attachment.size && ` • ${formatFileSize(attachment.size)}`}
                                                    </div>
                                                  </div>
                                                </div>
                                                <button
                                                  className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAttachmentClick(attachment, message);
                                                  }}
                                                >
                                                  Download
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Collapsible forwarded content */}
                                    {forwardedContent && (
                                      <details className="mt-4">
                                        <summary className="text-sm cursor-pointer text-blue-600 hover:text-blue-800 hover:underline">
                                          Show forwarded message
                                        </summary>
                                        <div className="mt-2 text-sm p-3 bg-gray-50 rounded-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                                          {forwardedContent}
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Reply Box */}
              <div className="border-t border-gray-200 px-6 py-4 bg-white">
                <div className="flex items-end space-x-4">
                  <div className="flex-1">
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder={selectedConversation?.type === 'sms' 
                        ? `Text ${selectedConversation.contact_name}...`
                        : `Reply to ${selectedConversation.contact_name}...`
                      }
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
                        selectedConversation?.type === 'sms' ? 'rounded-2xl' : ''
                      }`}
                      rows={selectedConversation?.type === 'sms' ? '2' : '3'}
                    />
                  </div>
                  <button
                    onClick={sendReply}
                    disabled={!replyContent.trim()}
                    className={`px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${
                      selectedConversation?.type === 'sms' ? 'rounded-full' : 'rounded-lg'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    <span>{selectedConversation?.type === 'sms' ? 'Send' : 'Reply'}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right Panel - Day Schedule (Dynamic responsive width) */}
      {!scheduleCollapsed && (
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col flex-shrink-0">
          {/* Schedule Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Today's Schedule</h2>
              <button
                onClick={() => setScheduleCollapsed(true)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                title="Collapse schedule"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            <div className="text-sm text-gray-600">
              {selectedDate.toLocaleDateString([], { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>

          {/* Day Schedule List */}
          <div className="flex-1 overflow-y-auto p-4">
            {daySchedule.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm">No tasks scheduled</p>
              </div>
            ) : (
              <div className="space-y-3">
                {daySchedule.map((task) => (
                  <div
                    key={task.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                        {task.name}
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatTaskTime(task)}</span>
                      </div>
                      <span className="capitalize">{task.status}</span>
                    </div>
                    
                    {task.estimated_effort && (
                      <div className="text-xs text-gray-400 mt-1">
                        Est: {task.estimated_effort}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Collapsed Schedule Toggle */}
      {scheduleCollapsed && (
        <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-10">
          <button
            onClick={() => setScheduleCollapsed(false)}
            className="p-3 bg-white border border-gray-200 rounded-lg shadow-lg hover:shadow-xl text-gray-600 hover:text-gray-900"
            title="Show schedule"
          >
            <Calendar className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Enhanced Compose Modal */}
      {composeMode && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Compose Message</h2>
                <button
                  onClick={() => setComposeMode(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 max-h-[calc(90vh-200px)] overflow-y-auto">
                {/* Message Type and Provider */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <select
                      value={newMessage.type}
                      onChange={(e) => setNewMessage({ ...newMessage, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                    <select
                      value={newMessage.provider}
                      onChange={(e) => setNewMessage({ ...newMessage, provider: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="gmail">Gmail</option>
                      <option value="outlook">Outlook</option>
                      <option value="gohighlevel">GoHighLevel</option>
                    </select>
                  </div>
                </div>

                {/* To Field */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={newMessage.to}
                    onChange={(e) => {
                      setNewMessage({ ...newMessage, to: e.target.value });
                      if (e.target.value.length >= 2) {
                        searchContacts(e.target.value, 'to');
                      } else {
                        setShowContactSuggestions(false);
                      }
                    }}
                    onFocus={() => {
                      if (newMessage.to.length >= 2) {
                        searchContacts(newMessage.to, 'to');
                      }
                    }}
                    onBlur={handleContactFieldBlur}
                    placeholder="Search contacts or enter email..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  
                  {/* Contact Suggestions Dropdown */}
                  {showContactSuggestions && activeSearchField === 'to' && contactSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {contactSuggestions.map((contact) => (
                        <div
                          key={contact.id}
                          onClick={() => selectContact(contact, 'to')}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                              {contact.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{contact.name}</div>
                              <div className="text-sm text-gray-500 truncate">
                                {contact.primary_contact}
                                {contact.organization_info && (
                                  <span className="text-gray-400">{contact.organization_info}</span>
                                )}
                              </div>
                              {contact.role && contact.role !== 'primary' && (
                                <div className="text-xs text-blue-600 truncate">{contact.role}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchingContacts && activeSearchField === 'to' && (
                    <div className="absolute right-3 top-10 text-gray-400">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>

                {/* CC/BCC Fields for Email */}
                {newMessage.type === 'email' && (
                  <>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => setShowCcBcc(!showCcBcc)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {showCcBcc ? 'Hide' : 'Add'} CC/BCC
                      </button>
                    </div>

                    {showCcBcc && (
                      <div className="grid grid-cols-2 gap-4">
                        {/* CC Field with Contact Search */}
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">CC</label>
                          <input
                            type="email"
                            value={newMessage.cc}
                            onChange={(e) => {
                              setNewMessage({ ...newMessage, cc: e.target.value });
                              if (e.target.value.length >= 2) {
                                searchContacts(e.target.value, 'cc');
                              } else {
                                setShowContactSuggestions(false);
                              }
                            }}
                            onFocus={() => {
                              if (newMessage.cc.length >= 2) {
                                searchContacts(newMessage.cc, 'cc');
                              }
                            }}
                            onBlur={handleContactFieldBlur}
                            placeholder="Search contacts or enter email..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          
                          {/* CC Contact Suggestions */}
                          {showContactSuggestions && activeSearchField === 'cc' && contactSuggestions.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {contactSuggestions.map((contact) => (
                                <div
                                  key={contact.id}
                                  onClick={() => selectContact(contact, 'cc')}
                                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                      {contact.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 truncate">{contact.name}</div>
                                      <div className="text-sm text-gray-500 truncate">
                                        {contact.primary_contact}
                                        {contact.organization_info && (
                                          <span className="text-gray-400">{contact.organization_info}</span>
                                        )}
                                      </div>
                                      {contact.role && contact.role !== 'primary' && (
                                        <div className="text-xs text-green-600 truncate">{contact.role}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {searchingContacts && activeSearchField === 'cc' && (
                            <div className="absolute right-3 top-10 text-gray-400">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            </div>
                          )}
                        </div>
                        
                        {/* BCC Field with Contact Search */}
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">BCC</label>
                          <input
                            type="email"
                            value={newMessage.bcc}
                            onChange={(e) => {
                              setNewMessage({ ...newMessage, bcc: e.target.value });
                              if (e.target.value.length >= 2) {
                                searchContacts(e.target.value, 'bcc');
                              } else {
                                setShowContactSuggestions(false);
                              }
                            }}
                            onFocus={() => {
                              if (newMessage.bcc.length >= 2) {
                                searchContacts(newMessage.bcc, 'bcc');
                              }
                            }}
                            onBlur={handleContactFieldBlur}
                            placeholder="Search contacts or enter email..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          
                          {/* BCC Contact Suggestions */}
                          {showContactSuggestions && activeSearchField === 'bcc' && contactSuggestions.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {contactSuggestions.map((contact) => (
                                <div
                                  key={contact.id}
                                  onClick={() => selectContact(contact, 'bcc')}
                                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                      {contact.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 truncate">{contact.name}</div>
                                      <div className="text-sm text-gray-500 truncate">
                                        {contact.primary_contact}
                                        {contact.organization_info && (
                                          <span className="text-gray-400">{contact.organization_info}</span>
                                        )}
                                      </div>
                                      {contact.role && contact.role !== 'primary' && (
                                        <div className="text-xs text-purple-600 truncate">{contact.role}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {searchingContacts && activeSearchField === 'bcc' && (
                            <div className="absolute right-3 top-10 text-gray-400">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Subject for Email */}
                {newMessage.type === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <input
                      type="text"
                      value={newMessage.subject}
                      onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                      placeholder="Subject line..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Message Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  {newMessage.type === 'email' ? (
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      {/* Rich Text Toolbar */}
                      <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
                        <button
                          type="button"
                          onClick={() => execCommand('bold')}
                          className="p-2 hover:bg-gray-200 rounded text-gray-700 hover:text-gray-900"
                          title="Bold"
                        >
                          <Bold className="w-4 h-4" />
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => execCommand('italic')}
                          className="p-2 hover:bg-gray-200 rounded text-gray-700 hover:text-gray-900"
                          title="Italic"
                        >
                          <Italic className="w-4 h-4" />
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => execCommand('underline')}
                          className="p-2 hover:bg-gray-200 rounded text-gray-700 hover:text-gray-900"
                          title="Underline"
                        >
                          <Underline className="w-4 h-4" />
                        </button>
                        
                        <div className="w-px h-6 bg-gray-300 mx-1"></div>
                        
                        <button
                          type="button"
                          onClick={() => execCommand('insertUnorderedList')}
                          className="p-2 hover:bg-gray-200 rounded text-gray-700 hover:text-gray-900"
                          title="Bullet List"
                        >
                          •
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => execCommand('insertOrderedList')}
                          className="p-2 hover:bg-gray-200 rounded text-gray-700 hover:text-gray-900"
                          title="Numbered List"
                        >
                          1.
                        </button>
                        
                        <div className="w-px h-6 bg-gray-300 mx-1"></div>
                        
                        <button
                          type="button"
                          onClick={insertLink}
                          className="p-2 hover:bg-gray-200 rounded text-gray-700 hover:text-gray-900"
                          title="Insert Link"
                        >
                          <Link2 className="w-4 h-4" />
                        </button>
                        
                        <select
                          onChange={(e) => execCommand('fontSize', e.target.value)}
                          className="ml-2 px-2 py-1 text-sm border border-gray-300 rounded"
                          title="Font Size"
                        >
                          <option value="3">Normal</option>
                          <option value="1">Small</option>
                          <option value="4">Large</option>
                          <option value="5">X-Large</option>
                        </select>
                        
                        <input
                          type="color"
                          onChange={(e) => execCommand('foreColor', e.target.value)}
                          className="ml-2 w-8 h-8 border border-gray-300 rounded cursor-pointer"
                          title="Text Color"
                        />
                      </div>
                      
                      {/* Rich Text Editor */}
                      <div
                        ref={editorRef}
                        contentEditable
                        onInput={handleEditorInput}
                        onPaste={handleEditorPaste}
                        className="p-3 min-h-[200px] max-h-[400px] overflow-y-auto focus:outline-none prose prose-sm max-w-none"
                        style={{ minHeight: '200px' }}
                        suppressContentEditableWarning={true}
                        data-placeholder="Type your message..."
                      />
                    </div>
                  ) : (
                    <textarea
                      value={newMessage.body}
                      onChange={(e) => setNewMessage({ ...newMessage, body: e.target.value })}
                      placeholder="Type your SMS message..."
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                      rows={4}
                    />
                  )}
                </div>

                {/* Attachments for Email */}
                {newMessage.type === 'email' && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div {...getRootProps()} className="cursor-pointer">
                      <input {...getInputProps()} />
                      <div className="text-center">
                        <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2">
                          {isDragActive ? (
                            <p className="text-gray-600">Drop files here...</p>
                          ) : (
                            <div>
                              <p className="text-gray-600">
                                <span className="font-medium text-blue-600 hover:text-blue-500">
                                  Click to upload
                                </span>
                                {' '}or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">Files up to 25MB</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Attachment List */}
                    {newMessage.attachments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Attachments:</h4>
                        {newMessage.attachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <div className="flex items-center space-x-2">
                              <Paperclip className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{attachment.name}</span>
                              <span className="text-xs text-gray-500">
                                ({(attachment.size / 1024 / 1024).toFixed(1)} MB)
                              </span>
                            </div>
                            <button
                              onClick={() => removeAttachment(attachment.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  {newMessage.type === 'email' && newMessage.attachments.length > 0 && (
                    <span>{newMessage.attachments.length} attachment(s)</span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setComposeMode(false);
                      setShowCcBcc(false);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.to || !newMessage.body.trim()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Send</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedInbox;
